import { Trans, useLingui } from "@lingui/react";
import type { WarpModal } from "@warp-ds/elements/modal";
import { Button } from "@warp-ds/elements/react/button";
import { Modal, ModalFooter, ModalHeader } from "@warp-ds/elements/react/modal";
import { type Ref, useImperativeHandle, useRef, useState } from "react";
import { useTracking } from "../../utils/pulseHelper.ts";

export type LoginModalHandle = {
  open: () => void;
};

export const LoginModal = ({ ref }: { ref?: Ref<LoginModalHandle> }) => {
  const modalRef = useRef<WarpModal>(null);
  const [nextUrl, setNextUrl] = useState("");
  const { i18n } = useLingui();

  const { trackLogin, trackCancelLoginModal } = useTracking();

  const openWith = () => {
    const { origin, pathname } = window.location;
    const url = new URL(`${origin}/auth/login`);
    const redirectUrl = `${origin}${pathname}`;

    url.searchParams.set("redirectUrl", redirectUrl);
    url.searchParams.set("cancelUrl", `${origin}${pathname}`);

    setNextUrl(url.toString());
    modalRef.current?.open();
  };

  useImperativeHandle(ref, () => ({
    open: openWith,
  }));

  const handleCancel = () => {
    modalRef.current?.close();
  };

  const onHidden = () => {
    trackCancelLoginModal();
  };

  const loginModalTitle = i18n.t({ id: "dealer.loginModal.fomo.title", message: "You're not the only one interested" });

  const loginModalText = i18n.t({
    id: "dealer.loginModal.reserve.description",
    message:
      "Log in to reserve the car before someone else does.\n" +
      "You can also save the ad or get notified when something similar appears.",
  });

  return (
    <Modal ref={modalRef} onHidden={onHidden}>
      <ModalHeader slot="header" title={loginModalTitle} />
      <div slot="content">
        <p>{loginModalText}</p>
      </div>
      <ModalFooter slot="footer">
        <div className="flex w-full md:w-[50%] ml-auto gap-8">
          <Button variant="quiet" fullWidth onClick={handleCancel}>
            <Trans id="dealer.loginModal.cancel" message="Not now" />
          </Button>
          <Button variant="primary" fullWidth href={nextUrl} onClick={() => trackLogin()}>
            <Trans id="dealer.loginModal.logIn" message="Log in" />
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
